// Aliased: both functions below shadow `metadata` with a local narrowing of
// `uimetadata`, and the graphics half is a separate document.
import { metadata as graphicsMetadata, uimetadata } from './metadata';
import { getAutoIntelRPOCode } from './autointel/autointel.service';

/**
 * Shape of a GM Catalog Mastersheet code: a namespace prefix plus digits —
 * `CHSL09`, `EX12`, `IN16`, `AC128`, `OP18`, `PA23`.
 *
 * Used to sanity-check the `autoIntelRPOCode` fallback only. On several
 * projects that field was populated with the item's raw configurationId
 * (`AF16FA35357F`, `61ED587F96AD`) rather than a catalogue code, and without
 * this gate those ids get spliced straight into the configured-vehicle code
 * that Salesforce parses positionally.
 */
const CATALOGUE_CODE = /^(AC|OP|PA|EX|IN|CH)[A-Z0-9]*\d+$/;

/**
 * Lead-file catalogue code for one entity, from the GM Catalog Mastersheet
 * (`CHCA01`, `EX49`, `IN17`, `OP01`, `AC94`). This is the code Salesforce and
 * the SFTP lead file expect, and it is a different namespace from
 * `autoIntelRPOCode`, which identifies the item to the AutoIntel
 * inventory/recommendation SDK.
 *
 * The `autoIntelRPOCode` fallback is transitional: only some projects carry
 * `brandItemCode` so far, and on several of the rest the AutoIntel field was
 * populated with catalogue codes during migration. It is gated on
 * `CATALOGUE_CODE` because on the remaining projects that field holds a
 * configurationId instead. An explicit `brandItemCode` is trusted as authored
 * and is never shape-checked. Drop the fallback once `brandItemCode` is
 * populated everywhere.
 */
export const getBrandItemCode = (
  itemId: string,
  variantId?: string
): string | undefined => {
  const metadata = uimetadata as unknown as Record<string, any>;
  for (const modelKey of Object.keys(metadata?.models || {})) {
    const model = metadata.models[modelKey];
    for (const group of [
      model?.variants,
      model?.colors?.exterior,
      model?.colors?.interior,
      model?.accessories,
      model?.packages,
    ]) {
      const code = group?.[itemId]?.brandItemCode;
      if (code) return code;
    }
  }
  const fallback = getAutoIntelRPOCode(itemId, variantId);
  return fallback && CATALOGUE_CODE.test(fallback) ? fallback : undefined;
};

interface ConfiguredVehicleCodeInput {
  variantID?: string;
  exteriorColorID?: string;
  interiorColorID?: string;
  accessoryIDs?: string[];
  packageIDs?: string[];
}

/**
 * Builds the dash-joined Salesforce configured-vehicle code, e.g.
 * `CHSPAEUV2026_03-GAZ-H2X-...`.
 *
 * Port of 1.0's `getConfiguredVehicleCode()` (src/utils/configurator.ts). The
 * segment order is fixed — variant, exterior colour, interior colour, then
 * accessories and packages in metadata order — because the backend splits the
 * code positionally. Model-level codes are intentionally excluded: the variant
 * code is the sole prefix.
 *
 * Segments come from `brandItemCode` — the Salesforce/lead-file catalogue code,
 * the same field 1.0 reads. NOT `autoIntelRPOCode`, which is the AutoIntel
 * SDK's own item id and a different namespace (on 2378 the two disagree for
 * every colour: `EX29` vs the real GM RPO `G4J`).
 */
export const getConfiguredVehicleCode = ({
  variantID,
  exteriorColorID,
  interiorColorID,
  accessoryIDs = [],
  packageIDs = [],
}: ConfiguredVehicleCodeInput): string => {
  const metadata = uimetadata as unknown as Record<string, any>;
  const modelKey = metadata?.config?.model || '';
  const model =
    metadata?.models?.[modelKey] ||
    metadata?.models?.[Object.keys(metadata?.models || {})[0]];

  const variantCode = variantID ? getBrandItemCode(variantID) || '' : '';
  const exteriorColorCode = exteriorColorID
    ? getBrandItemCode(exteriorColorID) || ''
    : '';
  const interiorColorCode = interiorColorID
    ? getBrandItemCode(interiorColorID, variantID) || ''
    : '';

  // Accessories the SDK auto-applies on variant load (`useInitialiseVariant`
  // seeds every `isDefault` entry into the accessory store, which is what the
  // save dialog reads). They are graphics states, not orderable line items —
  // typically the "without X" half of a pair, e.g. 2371's `h7l2djmpt06c`
  // WO_HUD, which was reporting a head-up display precisely because the truck
  // has none. Packages carry no `isDefault`, so this is a no-op for them.
  const graphics = graphicsMetadata as unknown as Record<string, any>;
  const graphicsModel =
    graphics?.models?.[modelKey] ||
    graphics?.models?.[Object.keys(graphics?.models || {})[0]];
  const isAutoApplied = (id: string) =>
    graphicsModel?.accessories?.[id]?.isDefault === true;

  // Iterate the metadata collections rather than the selection arrays so the
  // codes come out in a stable, metadata-defined order (1.0 walks
  // activeVariant.accessories / .packages for the same reason).
  const codesFrom = (collection: Record<string, unknown> | undefined, selected: string[]) =>
    Object.keys(collection || {})
      .filter((id) => selected.includes(id) && !isAutoApplied(id))
      .map((id) => getBrandItemCode(id, variantID) || '')
      .filter(Boolean);

  const accessoryCodes = codesFrom(model?.accessories, accessoryIDs);
  const packageCodes = codesFrom(model?.packages, packageIDs);

  return [
    variantCode,
    exteriorColorCode,
    interiorColorCode,
    ...accessoryCodes,
    ...packageCodes,
  ]
    .filter(Boolean)
    .join('-');
};
